/**
 * useHomeworkQuery — TanStack Query hook over the Lekser board feed (Fase 4).
 *
 * Backend (chores.routes GET /api/chores/homework → queryHomework) returns every
 * live homework instance for the session, due-date sorted (soonest first, nulls
 * last), enriched the same way as the pool board (age_gated, assignee surfacing)
 * plus homework-specific fields: `subject`, `due_at`, and `claimant_short_name`.
 *
 * The feed is server-side filtered + age/assignment-resolved against the viewer
 * (the route reads c.get('member')). Cache + retry mirror usePoolBoard; live
 * refresh is driven by useChoresWS invalidating the ['homework'] key on the
 * 'chores' WS topic.
 */
import { useQuery } from '@tanstack/react-query';
import type { PoolChoreInstance } from './useChoresQuery';

/** A homework card row as returned by GET /api/chores/homework. */
export type HomeworkInstance = PoolChoreInstance & {
  /** ISO date/datetime deadline copied from the template at seed time; nullable. */
  due_at: string | null;
  /** School subject for grouping ("Matematikk"); nullable. */
  subject: string | null;
  /** short_name of the member who claimed/completed it, or null when open. */
  claimant_short_name: string | null;
};

async function fetchJson<T>(url: string): Promise<T> {
  const r = await globalThis.fetch(url, { credentials: 'same-origin' });
  if (!r.ok) {
    const err = new Error(`fetch_failed:${r.status}`);
    (err as Error & { status: number }).status = r.status;
    throw err;
  }
  return (await r.json()) as T;
}

/** Lekser board feed for the current session. */
export function useHomework() {
  return useQuery<HomeworkInstance[]>({
    queryKey: ['homework'],
    queryFn: () => fetchJson<HomeworkInstance[]>('/api/chores/homework'),
    staleTime: 30_000,
    retry: 1,
  });
}
